import { Flex, Text } from '@mantine/core';
import {
  useChatComposer,
  type ChatOutgoingPayload,
} from '@packages/chat/react';
import {
  ChatAttachButton,
  ChatComposerInput,
  ChatSendButton,
} from '@packages/chat/ui';
import { showNotification } from '@shared/utils';
import { useState } from 'react';
import { useListenVoiceMessage } from '../hooks/useListenCandidateVoiceMessage';

const UNSUPPORTED_AUDIO_MESSAGE =
  'Можно отправить только аудиофайл OGG или WebM с кодеком Opus';

type CandidateInterviewChatMessageInputProps = {
  /** Resolves to whether the server accepted the message. */
  sendMessage: (payload: ChatOutgoingPayload) => Promise<boolean>;
  isSending: boolean;
};

export const CandidateInterviewChatMessageInput = ({
  sendMessage,
  isSending,
}: CandidateInterviewChatMessageInputProps) => {
  const { openCanditateVoiceMessage } = useListenVoiceMessage();
  const [audioFile, setAudioFile] = useState<File | null>(null);
  // The text stays in the field until the server accepts it, so the composer
  // is disabled while sending: a repeated Enter would duplicate the WhatsApp
  // message to the candidate.
  const composer = useChatComposer({
    send: text => sendMessage({ type: 'text', data: text }),
    disabled: isSending,
  });

  const handleFileSelect = (file: File) => {
    setAudioFile(file);
    const audioUrl = URL.createObjectURL(file);

    openCanditateVoiceMessage({
      audioUrl,
      fileName: file.name,
      type: 'upload',
      onSend: () => {
        void sendMessage({ type: 'audio', data: file });
        setAudioFile(null);
        URL.revokeObjectURL(audioUrl);
      },
      onClose: () => {
        URL.revokeObjectURL(audioUrl);
        setAudioFile(null);
      },
    });
  };

  const handleInvalidFile = () => {
    showNotification({ variant: 'error', message: UNSUPPORTED_AUDIO_MESSAGE });
  };

  return (
    <Flex direction='column' gap={16}>
      <Flex direction='column' gap={8}>
        <Text>Сообщение кандидату</Text>
        <Flex align='center'>
          <ChatComposerInput
            value={composer.text}
            onChange={event => composer.setText(event.currentTarget.value)}
            onKeyDown={composer.handleKeyDown}
            disabled={isSending}
            c='otherWhite.0'
            placeholder='Введите текст сообщения'
          />
          <ChatAttachButton
            title='Прикрепить аудио'
            disabled={isSending}
            value={audioFile}
            onSelect={handleFileSelect}
            onInvalidFile={handleInvalidFile}
          />
        </Flex>
      </Flex>
      <Flex align='center' gap={12}>
        <ChatSendButton
          onClick={() => void composer.submit()}
          disabled={!composer.hasText || isSending}
          loading={isSending}
          styles={{
            label: {
              fontSize: '14px',
              fontWeight: 600,
            },
          }}
        >
          Отправить
        </ChatSendButton>
        <Text size='sm' opacity='60%'>
          Формат аудиофайла: OGG Кодировка OPUS *
        </Text>
      </Flex>
    </Flex>
  );
};
